import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { User, Calendar, MessageCircle, CheckSquare, ClipboardList, RotateCcw } from 'lucide-react'
import { Card, CardBody } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Relationship, RelationshipStatus } from '@/types/matching'
import StarRating from './StarRating'

interface RelationshipCardProps {
  relationship: Relationship
  userRole: 'mentor' | 'mentee'
  onRate?: (relationship: Relationship) => void
  onReconnect?: (relationship: Relationship) => void
  onEndMentorship?: (relationship: Relationship) => void
}

const RelationshipCard: React.FC<RelationshipCardProps> = ({
  relationship,
  userRole,
  onRate,
  onReconnect,
  onEndMentorship
}) => {
  const [isConfirmingEnd, setIsConfirmingEnd] = useState(false)
  const navigate = useNavigate()

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const getStatusColor = (status: RelationshipStatus): string => {
    switch (status) {
      case RelationshipStatus.ACTIVE:
        return 'bg-green-100 text-green-700'
      case RelationshipStatus.PENDING:
        return 'bg-yellow-100 text-yellow-700'
      case RelationshipStatus.COMPLETED:
        return 'bg-accent-100 text-accent-700'
      case RelationshipStatus.CANCELLED:
        return 'bg-red-100 text-red-700'
      default:
        return 'bg-gray-100 text-gray-700'
    }
  }

  // Get the other person in the relationship
  const otherPerson = userRole === 'mentor' ? relationship.mentee : relationship.mentor
  const roleLabel = userRole === 'mentor' ? 'Mentee' : 'Mentor'

  // The rating I gave the other person: mentors' ratings are stored under
  // menteeRating, mentees' ratings under mentorRating (see matching-service's
  // rateRelationship - the field is named for who is *being rated*, not who
  // submitted it).
  const myRating = userRole === 'mentor' ? relationship.menteeRating : relationship.mentorRating

  if (!otherPerson) {
    return null
  }

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardBody>
        <div className="flex items-start gap-4">
          {/* Avatar */}
          <Link to={`/users/${otherPerson.id}`} className="flex-shrink-0">
            {otherPerson.profile?.profilePicture ? (
              <img
                src={otherPerson.profile.profilePicture}
                alt={`${otherPerson.firstName} ${otherPerson.lastName}`}
                className="w-12 h-12 rounded-full object-cover"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                <User className="w-6 h-6 text-primary-600" />
              </div>
            )}
          </Link>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold text-gray-900">
                    <Link to={`/users/${otherPerson.id}`} className="hover:underline hover:text-primary-700">
                      {otherPerson.firstName} {otherPerson.lastName}
                    </Link>
                  </h3>
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                      relationship.status
                    )}`}
                  >
                    {relationship.status}
                  </span>
                </div>
                <p className="text-sm text-gray-500">{otherPerson.email}</p>
                <p className="text-xs text-gray-400 mt-1">{roleLabel}</p>
              </div>
            </div>

            {/* Bio or Expertise */}
            {userRole === 'mentee' && relationship.mentor?.profile?.bio && (
              <p className="mt-2 text-sm text-gray-600 line-clamp-2">
                {relationship.mentor.profile.bio}
              </p>
            )}

            {/* Expertise tags for mentor */}
            {userRole === 'mentee' && relationship.mentor?.profile?.expertise && (
              <div className="flex flex-wrap gap-1 mt-2">
                {relationship.mentor.profile.expertise.slice(0, 4).map((skill) => (
                  <span
                    key={skill}
                    className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            )}

            {/* Interests/Goals for mentee */}
            {userRole === 'mentor' && relationship.mentee?.profile && (
              <div className="flex flex-wrap gap-1 mt-2">
                {relationship.mentee.profile.interests?.slice(0, 3).map((interest) => (
                  <span
                    key={interest}
                    className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs"
                  >
                    {interest}
                  </span>
                ))}
                {relationship.mentee.profile.goals?.slice(0, 2).map((goal) => (
                  <span
                    key={goal}
                    className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs"
                  >
                    {goal}
                  </span>
                ))}
              </div>
            )}

            {/* Date info */}
            <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
              {relationship.status === RelationshipStatus.ACTIVE && relationship.acceptedAt && (
                <div className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  <span>Active since {formatDate(relationship.acceptedAt)}</span>
                </div>
              )}
              {relationship.status === RelationshipStatus.PENDING && (
                <div className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  <span>Requested {formatDate(relationship.requestedAt)}</span>
                </div>
              )}
              {relationship.status === RelationshipStatus.COMPLETED && relationship.completedAt && (
                <div className="flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  <span>Completed {formatDate(relationship.completedAt)}</span>
                </div>
              )}
            </div>

            {/* Actions for active relationships */}
            {relationship.status === RelationshipStatus.ACTIVE && (
              <>
                <div className="flex gap-2 mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/messages?relationshipId=${relationship.id}`)}
                  >
                    <MessageCircle className="w-4 h-4 mr-1" />
                    Message
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => navigate('/tasks')}>
                    <CheckSquare className="w-4 h-4 mr-1" />
                    View Tasks
                  </Button>
                  <Link to={`/plans/relationship/${relationship.id}`}>
                    <Button variant="outline" size="sm">
                      <ClipboardList className="w-4 h-4 mr-1" />
                      Plans
                    </Button>
                  </Link>
                </div>

                {/* Your rating, once submitted, while awaiting the other party */}
                {myRating?.ratedAt && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-500 mb-1">Your rating</p>
                    <StarRating value={myRating.score} readOnly size="sm" />
                    {myRating.review && (
                      <p className="mt-1 text-sm text-gray-600 italic">"{myRating.review}"</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      Awaiting {roleLabel.toLowerCase()}'s rating to finish
                    </p>
                  </div>
                )}

                {/* End Mentorship trigger, gated behind an inline confirmation */}
                {onEndMentorship && !myRating?.ratedAt && (
                  <div className="mt-3 flex justify-end">
                    {isConfirmingEnd ? (
                      <div className="text-right">
                        <p className="text-sm text-gray-600 mb-2">
                          End this mentorship? You'll be asked to rate your{' '}
                          {roleLabel.toLowerCase()} first, and it can't be undone.
                        </p>
                        <div className="flex gap-2 justify-end">
                          <Button variant="ghost" size="sm" onClick={() => setIsConfirmingEnd(false)}>
                            Cancel
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => {
                              setIsConfirmingEnd(false)
                              onEndMentorship(relationship)
                            }}
                          >
                            Yes, end it
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="text-sm text-red-600 hover:text-red-700 hover:underline"
                        onClick={() => setIsConfirmingEnd(true)}
                      >
                        End Mentorship
                      </button>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Rating & reconnection for completed relationships */}
            {relationship.status === RelationshipStatus.COMPLETED && (
              <div className="mt-3">
                {myRating?.ratedAt ? (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Your rating</p>
                    <div className="flex items-center gap-2">
                      <StarRating value={myRating.score} readOnly size="sm" />
                    </div>
                    {myRating.review && (
                      <p className="mt-1 text-sm text-gray-600 italic">"{myRating.review}"</p>
                    )}
                  </div>
                ) : (
                  onRate && (
                    <Button variant="outline" size="sm" onClick={() => onRate(relationship)}>
                      Rate {roleLabel}
                    </Button>
                  )
                )}
                {userRole === 'mentee' && onReconnect && (
                  <div className="mt-2">
                    <Button variant="outline" size="sm" onClick={() => onReconnect(relationship)}>
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Request Again
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </CardBody>
    </Card>
  )
}

export default RelationshipCard
